import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Search } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { wmMessage } from "@/lib/wmError";
import { ItemFormDialog } from "@/components/waste-meals/admin/ItemFormDialog";
import { isRawItem } from "@/components/waste-meals/wmUtils";

const C = OPS.wm.admin;
const USAGE_FILTERS = ["all", "waste", "meals", "both"];

/** «إدارة الأصناف» — the item table: code, AR/EN name, usage, unit, state. */
export function ItemsAdmin({ units }) {
    const { t, lang } = useI18n();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [usage, setUsage] = useState("all");
    const [onlyActive, setOnlyActive] = useState(false);
    const [editing, setEditing] = useState(null);   // item | {} for create | null

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/waste-meals/items", { params: { include_inactive: 1 } });
            setRows(r.data.rows || []);
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setLoading(false);
        }
    }, [lang, t]);

    useEffect(() => { load(); }, [load]);

    const shown = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return rows.filter((r) => {
            if (onlyActive && !r.is_active) return false;
            if (usage !== "all" && r.usage !== usage) return false;
            if (!needle) return true;
            return [r.code, r.name_ar, r.name_en, r.category, r.sc_item_code]
                .some((v) => (v || "").toLowerCase().includes(needle));
        });
    }, [rows, q, usage, onlyActive]);

    const counts = useMemo(() => ({
        total: rows.length,
        active: rows.filter((r) => r.is_active).length,
    }), [rows]);

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                    <Search className="pointer-events-none absolute top-2.5 h-4 w-4 text-[color:var(--ink-faint)] start-2.5" />
                    <Input data-testid={C.itemSearch} value={q} onChange={(e) => setQ(e.target.value)}
                           placeholder={t("wm_admin_search")} className="h-9 w-52 ps-8" />
                </div>
                <div className="flex gap-1 rounded-full border border-[color:var(--border-soft)] p-0.5">
                    {USAGE_FILTERS.map((u) => (
                        <button key={u} type="button" data-testid={C.usageFilter(u)}
                                onClick={() => setUsage(u)}
                                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                                    usage === u
                                        ? "bg-[color:var(--brand-orange)] text-white"
                                        : "text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]"
                                }`}>
                            {t(`wm_admin_usage_${u}`)}
                        </button>
                    ))}
                </div>
                <label className="flex items-center gap-2 text-xs font-semibold text-[color:var(--ink-muted)]">
                    <Switch data-testid={C.onlyActive} checked={onlyActive} onCheckedChange={setOnlyActive} />
                    {t("wm_admin_only_active")}
                </label>
                <Badge variant="secondary" className="h-7 px-2.5 text-[11px]">
                    {t("wm_admin_count")}: <bdi dir="ltr" className="ms-1">{counts.active}/{counts.total}</bdi>
                </Badge>
                <Button data-testid={C.itemAdd} size="sm" onClick={() => setEditing({})}
                        className="ms-auto h-9 gap-1.5 text-xs font-semibold">
                    <Plus className="h-4 w-4" />
                    {t("wm_admin_item_add")}
                </Button>
            </div>

            {loading ? <InlineLoader /> : !shown.length ? (
                <p data-testid={C.itemsEmpty}
                   className="rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] py-10 text-center text-sm text-[color:var(--ink-muted)]">
                    {t("wm_admin_items_empty")}
                </p>
            ) : (
                <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)]">
                    <table data-testid={C.itemsTable} className="w-full min-w-[520px] text-sm">
                        <thead className="bg-[color:var(--bg-surface-2)]">
                            <tr>
                                {[["wm_admin_code"], ["wm_admin_name_ar"], ["wm_admin_name_en", true],
                                  ["wm_admin_usage"], ["wm_admin_unit"], ["wm_admin_state"], [""]].map(([h, wide], i) => (
                                    <th key={i} className={`px-3 py-2 text-start text-xs font-bold text-[color:var(--ink-muted)] ${
                                        wide ? "hidden sm:table-cell" : ""}`}>
                                        {h ? t(h) : ""}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {shown.map((r) => (
                                <tr key={r.id} data-testid={C.itemRow(r.id)}
                                    className="border-t border-[color:var(--separator)]">
                                    <td className="px-3 py-2.5 text-xs tabular-nums text-[color:var(--ink-muted)]">
                                        <bdi dir="ltr">{r.code}</bdi>
                                    </td>
                                    <td className="px-3 py-2.5 font-semibold text-[color:var(--ink)]">
                                        {r.name_ar}
                                        {isRawItem(r) ? (
                                            <span data-testid={C.itemRawBadge(r.id)}
                                                  className="ms-1.5 inline-flex rounded-full bg-[color:var(--brand-brown-soft)] px-1.5 py-px align-middle text-[10px] font-semibold text-[color:var(--brand-brown-soft-fg)]">
                                                {t("wm_raw_badge")}
                                            </span>
                                        ) : null}
                                        {isRawItem(r) && r.sc_item_code ? (
                                            <span className="block text-[10px] font-normal text-[color:var(--ink-faint)]">
                                                <bdi dir="ltr">{r.sc_item_code}</bdi>
                                            </span>
                                        ) : null}
                                    </td>
                                    <td className="hidden px-3 py-2.5 text-xs text-[color:var(--ink-muted)] sm:table-cell">{r.name_en || "—"}</td>
                                    <td className="px-3 py-2.5 text-xs">
                                        <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                                            {t(`wm_admin_usage_${r.usage}`)}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2.5 text-xs text-[color:var(--ink-muted)]">
                                        {lang === "ar" ? r.unit_name_ar : r.unit_name_en || r.unit_name_ar}
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span data-testid={C.itemState(r.id)}
                                              className={`text-[11px] font-bold ${
                                                  r.is_active
                                                      ? "text-[color:var(--success)]"
                                                      : "text-[color:var(--ink-faint)]"}`}>
                                            {t(r.is_active ? "wm_admin_active" : "wm_admin_inactive")}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2.5 text-end">
                                        <Button data-testid={C.itemEdit(r.id)} variant="ghost" size="sm"
                                                onClick={() => setEditing(r)} className="h-7 gap-1 px-2 text-[11px]">
                                            <Pencil className="h-3.5 w-3.5" />
                                            {t("edit")}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing ? (
                <ItemFormDialog item={editing} units={units}
                                onClose={() => setEditing(null)}
                                onSaved={() => { setEditing(null); load(); }} />
            ) : null}
        </div>
    );
}
